import ModalBackdrop from './ModalBackdrop'
import ModalHeader from './ModalHeader'
import ScoringDemo from '../features/rules/demos/ScoringDemo'
import { formatNoticeDate, type UpdateNoticeVisual } from '../updateNotice'
import type { UpdateNoticeController } from '../app/useUpdateNotice'
import './UpdateNoticeModal.css'

/** The illustration slot: an entry names a key, this resolves it. Every option is a demo the app
 *  already ships — a release note draws nothing of its own, same rule as How to Play. `alive` is
 *  the one difference from the rules page: the cards run their real motion here, because a notice
 *  about new colours and animations that showed neither would be lying. */
function Visual({ visual }: { visual: UpdateNoticeVisual }) {
  switch (visual) {
    case 'scoring-cards':
      return <ScoringDemo alive />
  }
}

/**
 * The once-per-release "here is what changed" panel. Content comes from `UPDATE_NOTICES`
 * (src/updateNotice.ts), so announcing the next release is a data change, not a component.
 *
 * Every way out is the same way out: the backdrop, the `✕` and the button all dismiss for good.
 * There is deliberately no "remind me later" — a release note that can come back is a nag.
 */
export default function UpdateNoticeModal({
  controller,
}: {
  controller: UpdateNoticeController
}) {
  const { notice, dismiss } = controller
  if (!notice) return null

  return (
    // The notice is the whole point of the screen while it is up, so it takes the heavier scrim.
    // Calm pace because it opens itself: nobody tapped for this, so it eases in.
    <ModalBackdrop onClose={dismiss} scrim="immersive" pace="calm">
      {(close) => (
        <div className="update-notice">
          <ModalHeader title="Update" onClose={() => close()} />
          <p className="label-eyebrow update-notice__date">{formatNoticeDate(notice.date)}</p>

          <p className="update-notice__title">{notice.title}</p>

          {notice.visual && <Visual visual={notice.visual} />}

          {notice.body.map((paragraph) => (
            <p key={paragraph} className="update-notice__body">
              {paragraph}
            </p>
          ))}

          <button type="button" className="update-notice__action" onClick={() => close()}>
            {notice.cta ?? 'Got it'}
          </button>
        </div>
      )}
    </ModalBackdrop>
  )
}
